import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listBonuses,
  createBonus,
  approveBonus,
  reverseBonus,
  getBonusesWorkspace,
} from '@/api/services/bonuses.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  CreateBonusDto,
  BonusListQuery,
  MyBonusListQuery,
} from '@/features/bonuses/types/bonus.types'

export const bonusKeys = {
  all: (companyId: string | null) => ['bonuses', companyId] as const,
  list: (companyId: string | null, params: BonusListQuery) =>
    ['bonuses', companyId, 'list', params] as const,
  workspace: (params: MyBonusListQuery | undefined) =>
    ['bonuses', 'workspace', params] as const,
}

export function useBonusesWorkspace(
  params: MyBonusListQuery | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: bonusKeys.workspace(params),
    queryFn: () => getBonusesWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useBonusList(
  companyId: string | null,
  params: BonusListQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: bonusKeys.list(companyId, params),
    queryFn: () => listBonuses(companyId!, params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useCreateBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreateBonusDto }) =>
      createBonus(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bonusKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['bonuses', 'workspace'] })
      // Self-service list uses its own root key; bonuses also feed payroll runs.
      queryClient.invalidateQueries({ queryKey: ['bonuses-me'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Bonus issued.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useApproveBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string; id: string }) =>
      approveBonus(companyId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bonusKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['bonuses', 'workspace'] })
      // Self-service list uses its own root key; bonuses also feed payroll runs.
      queryClient.invalidateQueries({ queryKey: ['bonuses-me'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Bonus approved.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useReverseBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string; id: string }) =>
      reverseBonus(companyId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bonusKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['bonuses', 'workspace'] })
      // Self-service list uses its own root key; bonuses also feed payroll runs.
      queryClient.invalidateQueries({ queryKey: ['bonuses-me'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Bonus reversed.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
